<template>
  <page
    v-model:active-tab="activeTab"
    title="Minha empresa"
    :is-table="[usersRoute, billingRoute, limitsRoute].includes(activeTab)"
  >
    <template #tabs>
      <bg-tab :key="companyRoute">
        Dados da empresa
      </bg-tab>
      <bg-tab :key="usersRoute">
        Usuários
      </bg-tab>
      <bg-tab
        v-if="shouldShowBillingTabs"
        :key="billingRoute"
      >
        Faturas
      </bg-tab>
      <bg-tab
        id="account-limits-tab"
        :key="limitsRoute"
      >
        Meus limites
      </bg-tab>
    </template>
    <template #panels>
      <bg-tab-panel :key="companyRoute">
        <company-data-container />
      </bg-tab-panel>
      <bg-tab-panel :key="usersRoute">
        <users-container />
      </bg-tab-panel>
      <bg-tab-panel
        v-if="shouldShowBillingTabs"
        :key="billingRoute"
      >
        <billing-container />
      </bg-tab-panel>
      <bg-tab-panel :key="limitsRoute">
        <limits-container />
      </bg-tab-panel>
    </template>
  </page>
</template>

<script setup>
import { BgTab, BgTabPanel } from '@transfeeradev/bridge';
import CompanyDataContainer from '../company/components/container/CompanyDataContainer.vue';
import UsersContainer from '@/apps/admin/users/components/container/UsersContainer.vue';
import BillingContainer from '@/apps/admin/billing/components/container/BillingContainer.vue';
import LimitsContainer from '@/apps/admin/limits/components/container/LimitsContainer.vue';
import Page from '@/commons/components/presentational/Page.vue';
import { ref, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import loggedUser from '@/commons/resources/loggedUser';
import appSettingsResource from '@/apps/payments/resources/settings';
import { useTour } from '@/commons/composables/useTour';
import checkpoint from '@/commons/constants/checkpoint';

const emit = defineEmits(['loading']);
const router = useRouter();
const activeTab = ref(router.currentRoute.value.name);
const companyRoute = 'AdminCompanyDataPage';
const usersRoute = 'AdminUsersPage';
const billingRoute = 'BillingPage';
const limitsRoute = 'AdminLimitsPage';
const shouldShowBillingTabs = ref(false);

const setAllowedBillingTabVisibility = async () => {
  if (!loggedUser.isAdmin()) {
    return;
  }

  const settings = await appSettingsResource.get();

  shouldShowBillingTabs.value = settings.showFinanceInfo;
};

setAllowedBillingTabVisibility();

emit('loading', false);

watch(activeTab, name => {
  router.push({ name });
});

onMounted(() => {
  const { defineSteps } = useTour({
    checkpointId: checkpoint.ACCOUNT_LIMITS_VIEWED,
    elementSelectors: ['#account-limits-tab', '.limits-detail', '.limits-card', '#limits-card-menu-button']
  });

  defineSteps(
    ([step1El, step2El, step3El, step4El]) => [
      {
        element: step1El,
        title: 'Consulte aqui os seus limites',
        intro: 'Agora você pode consultar os limites das transações definidos para sua conta.'
      },
      {
        element: step2El,
        title: 'Limites agrupados por método de pagamento',
        intro: 'Os limites são exibidos de forma agrupada por método de pagamento.'
      },
      {
        element: step3El,
        title: 'Limites exibidos por cada tipo de configuração',
        intro: 'Você pode ver os limites definidos para cada tipo de configuração.'
      },
      {
        element: step4El,
        title: 'Edição de limites',
        intro: 'Em breve, você poderá editar os limites da sua empresa diretamente por aqui.'
      }
    ],
    {
      doneLabel: 'Entendi'
    }
  ).start();
});
</script>
